// Validation utility functions

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// International phone number validation (with country code)
const PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

// US zipcode validation (5 or 9 digit format)
const ZIPCODE_REGEX = /^\d{5}(?:-\d{4})?$/;

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates an email address
 * @param email - The email address to validate
 * @returns ValidationResult with isValid boolean and optional error message
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || email.trim() === "") {
    return {
      isValid: false,
      error: "Email is required",
    };
  }

  if (!EMAIL_REGEX.test(email.trim())) {
    return {
      isValid: false,
      error: "Please enter a valid email address",
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validates an international phone number (with country code)
 * @param phone - The phone number to validate
 * @returns ValidationResult with isValid boolean and optional error message
 */
export const validatePhone = (phone: string): ValidationResult => {
  if (!phone || phone.trim() === "") {
    return {
      isValid: false,
      error: "Phone number is required",
    };
  }

  // Check if it's a valid international phone number format
  if (!PHONE_REGEX.test(phone.trim())) {
    return {
      isValid: false,
      error: "Please enter a valid phone number with country code",
    };
  }

  // Additional check for length (should be between 7 and 15 digits after country code)
  const digitsOnly = phone.replace(/[^\d]/g, "");
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return {
      isValid: false,
      error: "Phone number must be between 7 and 15 digits",
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validates a required field
 * @param value - The value to validate
 * @param fieldName - The name of the field for error message
 * @returns ValidationResult with isValid boolean and optional error message
 */
export const validateRequired = (
  value: string,
  fieldName: string
): ValidationResult => {
  if (!value || value.trim() === "") {
    return {
      isValid: false,
      error: `${fieldName} is required`,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validates multiple fields at once
 * @param validations - Array of validation functions and their values
 * @returns Object with field names as keys and ValidationResult as values
 */
export const validateFields = (
  validations: Array<{
    field: string;
    value: string;
    validator: (value: string) => ValidationResult;
  }>
): Record<string, ValidationResult> => {
  const results: Record<string, ValidationResult> = {};

  validations.forEach(({ field, value, validator }) => {
    results[field] = validator(value);
  });

  return results;
};

/**
 * Checks if all validations are valid
 * @param validationResults - Object with validation results
 * @returns boolean indicating if all validations passed
 */
export const areAllValid = (
  validationResults: Record<string, ValidationResult>
): boolean => {
  return Object.values(validationResults).every((result) => result.isValid);
};

/**
 * Validates a US zipcode (5 or 9 digit format)
 * @param zipcode - The zipcode to validate
 * @returns ValidationResult with isValid boolean and optional error message
 */
export const validateZipcode = (zipcode: string): ValidationResult => {
  if (!zipcode || zipcode.trim() === "") {
    return {
      isValid: false,
      error: "Zipcode is required",
    };
  }

  if (!ZIPCODE_REGEX.test(zipcode.trim())) {
    return {
      isValid: false,
      error:
        "Invalid zipcode format. Must be 5 digits (XXXXX) or 9 digits (XXXXX-XXXX)",
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Gets all error messages from validation results
 * @param validationResults - Object with validation results
 * @returns Array of error messages
 */
export const getErrorMessages = (
  validationResults: Record<string, ValidationResult>
): string[] => {
  return Object.values(validationResults)
    .filter((result) => !result.isValid && result.error)
    .map((result) => result.error!);
};
